import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { euros, frDate } from "@/lib/weddingApi";
import { EVENT_TYPES, typeOf, SERVICE_LABELS } from "@/lib/eventTypes";
import {
  ArrowLeft, Copy, KeyRound, Mail, Printer, Download, RotateCcw, Save, Unlock, History, Check, Trash2,
} from "lucide-react";

const TABS = [
  { key: "contract", label: "Contrat" },
  { key: "guests", label: "Invités & menus" },
  { key: "access", label: "Accès & invitation" },
  { key: "kitchen", label: "Récap cuisine" },
  { key: "history", label: "Historique" },
];

const STATUS_LABELS = {
  draft: "Brouillon",
  access_sent: "Accès envoyé",
  in_progress: "En cours",
  validated: "Validé",
  cancelled: "Annulé",
};

const toEuros = (c) => ((c || 0) / 100).toFixed(2);
const toCents = (v) => Math.round(parseFloat(String(v).replace(",", ".") || 0) * 100);

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

export default function WeddingDetail() {
  const { id } = useParams();
  const [w, setW] = useState(null);
  const [tab, setTab] = useState("contract");
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [link, setLink] = useState("");
  const [kitchen, setKitchen] = useState(null);
  const [audit, setAudit] = useState([]);
  const [catalog, setCatalog] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/weddings/${id}`);
      setW(data);
      const c = data.contract || {};
      const sc = data.service_config || {};
      const dl = data.deadline || {};
      const type = typeOf(data.event_type);
      setForm({
        event_type: data.event_type || "",
        event_type_other: data.event_type_other || "",
        event_title: data.event_title || "",
        organizer_name: data.organizer_name || "",
        detail_value: type?.detail ? (data.event_details?.[type.detail.key] || "") : "",
        adult_services: sc.adult_services || ["starter", "main", "dessert"],
        child_services: sc.child_services || ["main", "dessert"],
        max_proposals_per_service: sc.max_proposals_per_service ?? 2,
        collective_mode: !!sc.collective_mode,
        deadline_mode: dl.mode || "relative",
        deadline_days_before: dl.days_before ?? 10,
        finalization_deadline: (dl.finalization_deadline || "").slice(0, 10),
        deadline_reason: dl.reason || "",
        couple_names: data.couple_names, contact_email: data.contact_email,
        contact_phone: data.contact_phone || "", event_date: data.event_date,
        arrival_time: data.arrival_time || "", start_time: data.start_time || "",
        end_time: data.end_time || "", adult_price: toEuros(c.adult_price_cents),
        child_price: toEuros(c.child_price_cents), child_max_age: c.child_max_age ?? 12,
        cocktail_price: toEuros(c.cocktail_price_cents), cheese_price: toEuros(c.cheese_price_cents),
        extra_half_hour: toEuros(c.extra_half_hour_cents), included_minutes: c.included_minutes ?? 180,
        extra_minutes: c.extra_minutes ?? 0, adults_count: c.adults_count ?? 0,
        children_count: c.children_count ?? 0, cocktail_participants: c.cocktail_participants ?? 0,
        cheese_participants: c.cheese_participants ?? 0, min_billable_adults: c.min_billable_adults ?? 0,
        min_billable_children: c.min_billable_children ?? 0, deposit_percent: c.deposit_percent ?? 50,
        space: c.space || "", space_confirmed: !!c.space_confirmed, end_time_max: c.end_time_max || "",
        balance_terms: c.balance_terms || "", deposit_terms: c.deposit_terms || "",
        conditions_text: c.conditions_text || "", internal_notes: data.internal_notes || "",
        operations_notes: data.operations_notes || "",
        acceptance_proof: data.acceptance?.proof_excerpt || "",
        acceptance_confirmed: !!data.acceptance?.accepted,
        deposit_received: !!data.deposit?.received,
        deposit_amount: toEuros(data.deposit?.amount_cents),
      });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/wedding-catalog").then(({ data }) => setCatalog(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (tab === "kitchen") api.get(`/admin/weddings/${id}/kitchen`).then(({ data }) => setKitchen(data)).catch(() => {});
    if (tab === "history") api.get(`/admin/weddings/${id}/audit`).then(({ data }) => setAudit(data)).catch(() => {});
  }, [tab, id, w?.version]);

  const patch = async (payload, successMsg = "Enregistré") => {
    setError(""); setMsg("");
    try {
      const { data } = await api.patch(`/admin/weddings/${id}`, { expected_version: w.version, ...payload });
      setW(data);
      setMsg(successMsg);
      return data;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      return null;
    }
  };

  const saveContract = async () => {
    const f = form;
    const type = typeOf(f.event_type);
    const details = { ...(w.event_details || {}) };
    if (type?.detail) details[type.detail.key] = f.detail_value;
    const res = await patch({
      event_type: f.event_type || undefined,
      event_type_other: f.event_type_other,
      event_title: f.event_title,
      organizer_name: f.organizer_name,
      event_details: details,
      service_config: {
        adult_services: f.adult_services,
        child_services: f.child_services,
        max_proposals_per_service: Number(f.max_proposals_per_service) || 2,
        collective_mode: !!f.collective_mode,
      },
      deadline: {
        mode: f.deadline_mode,
        days_before: Number(f.deadline_days_before) || 0,
        finalization_deadline: f.deadline_mode === "manual" ? f.finalization_deadline : "",
        reason: f.deadline_reason || "",
      },
      contact_email: f.contact_email, contact_phone: f.contact_phone,
      event_date: f.event_date, arrival_time: f.arrival_time, start_time: f.start_time, end_time: f.end_time,
      internal_notes: f.internal_notes, operations_notes: f.operations_notes,
      acceptance_proof: f.acceptance_proof, acceptance_confirmed: f.acceptance_confirmed,
      deposit_received: f.deposit_received, deposit_amount_cents: toCents(f.deposit_amount),
      contract: {
        adult_price_cents: toCents(f.adult_price), child_price_cents: toCents(f.child_price),
        child_max_age: Number(f.child_max_age) || 12,
        cocktail_price_cents: toCents(f.cocktail_price), cheese_price_cents: toCents(f.cheese_price),
        extra_half_hour_cents: toCents(f.extra_half_hour), included_minutes: Number(f.included_minutes) || 0,
        extra_minutes: Number(f.extra_minutes) || 0, adults_count: Number(f.adults_count) || 0,
        children_count: Number(f.children_count) || 0,
        cocktail_participants: Number(f.cocktail_participants) || 0,
        cheese_participants: Number(f.cheese_participants) || 0,
        min_billable_adults: Number(f.min_billable_adults) || 0,
        min_billable_children: Number(f.min_billable_children) || 0,
        deposit_percent: Number(f.deposit_percent) || 0, privatisation_cents: 0,
        space: f.space, space_confirmed: f.space_confirmed, end_time_max: f.end_time_max,
        balance_terms: f.balance_terms, deposit_terms: f.deposit_terms, conditions_text: f.conditions_text,
        inclusions: w.contract?.inclusions || [], exclusions: w.contract?.exclusions || [],
      },
    }, "Contrat enregistré");
    if (res) load();
  };

  const access = async (action) => {
    setError(""); setMsg("");
    try {
      const { data } = await api.post(`/admin/weddings/${id}/access`, { action });
      if (data.link) setLink(data.link);
      setMsg(action === "revoke" ? "Accès révoqué" : "Nouveau lien généré");
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendInvitation = async () => {
    if (!window.confirm(`Envoyer l'accès à ${w.contact_email} ?`)) return;
    setError(""); setMsg("");
    try {
      const key = (window.crypto?.randomUUID?.() || String(Date.now())).slice(0, 36);
      const { data } = await api.post(`/admin/weddings/${id}/send-invitation`, { idempotency_key: key, message: "" });
      setLink(data.link);
      setMsg(data.state === "sent" ? `Invitation envoyée à ${data.sent_to}` : `Envoi non confirmé (${data.state}) — ${data.error || "voir l'historique"}`);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reopen = async () => {
    const reason = window.prompt("Motif de réouverture (tracé dans l'historique) :");
    if (!reason) return;
    try {
      await api.post(`/admin/weddings/${id}/reopen`, { reason });
      setMsg("Dossier réouvert");
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadCsv = async () => {
    const res = await api.get(`/admin/weddings/${id}/export.csv`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cuisine-${w.reference}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resolveCr = async (crId, state) => {
    try {
      await api.post(`/admin/weddings/${id}/change-requests/${crId}`, { state, reply: "" });
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!w || !form) return <p className="text-sm text-[#697585]">{error || "Chargement…"}</p>;

  const childItems = catalog.filter((c) => c.course !== "starter");
  const labels = Object.fromEntries(catalog.map((c) => [c.id, c.label]));

  return (
    <div data-testid="wedding-detail-page">
      <Link to="/admin/receptions" className="inline-flex items-center gap-2 text-sm text-[#697585] hover:text-[#B8956A]">
        <ArrowLeft size={14} /> Toutes les réceptions
      </Link>

      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">
            {w.reference} · <span className="text-[#B8956A]">{w.event_type_label}</span>
            {w.is_demo ? " · Démonstration" : ""}
          </p>
          <h1 className="font-display text-3xl md:text-4xl mt-2 font-light">{w.event_title}</h1>
          <p className="mt-2 text-sm text-[#697585]">
            {frDate(w.event_date)}{w.organizer_name ? ` · ${w.organizer_name}` : ""} ·
            {" "}{w.counts?.adults || 0} adultes, {w.counts?.children || 0} enfants ·
            budget {euros(w.budget?.total_cents)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {w.status === "validated" && (
            <button data-testid="wedding-reopen-btn" className="btn-ghost inline-flex items-center gap-2" onClick={reopen}>
              <Unlock size={14} /> Réouvrir
            </button>
          )}
          <button
            data-testid="wedding-delete-btn"
            className="btn-ghost inline-flex items-center gap-2 hover:border-red-400 hover:text-red-500"
            onClick={async () => {
              if (!window.confirm(`Supprimer définitivement le dossier ${w.reference} ?`)) return;
              await api.delete(`/admin/weddings/${id}`);
              window.location.href = "/admin/receptions";
            }}
          >
            <Trash2 size={14} /> Supprimer
          </button>
          <span className={`px-3 py-2 text-xs uppercase tracking-[0.16em] border ${w.status === "validated" ? "border-emerald-600 text-emerald-700" : "border-[#B8956A] text-[#B8956A]"}`}>
            {STATUS_LABELS[w.status] || w.status}{w.late ? " · J-10 dépassé" : ""}
          </span>
        </div>
      </div>

      {(msg || error) && (
        <p data-testid="wedding-detail-feedback" className={`mt-5 text-sm ${error ? "text-red-600" : "text-emerald-700"}`}>
          {error || msg}
        </p>
      )}

      <div className="mt-8 flex flex-wrap gap-2 border-b border-[#D9D3C5] pb-3">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`wedding-detail-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm ${tab === t.key ? "text-[#B8956A] border-b-2 border-[#B8956A]" : "text-[#203240]/70"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "contract" && (
        <div className="mt-8 space-y-8">
          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Identité du dossier</h2>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Type d'événement">
                <select data-testid="detail-event-type" className="field w-full" value={form.event_type}
                        onChange={(e) => setForm({ ...form, event_type: e.target.value })}>
                  <option value="">— Choisir —</option>
                  {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              {form.event_type === "other" && (
                <Field label="Précision du type">
                  <input className="field w-full" value={form.event_type_other}
                         onChange={(e) => setForm({ ...form, event_type_other: e.target.value })} />
                </Field>
              )}
              <Field label="Titre de la réception">
                <input data-testid="detail-event-title" className="field w-full" value={form.event_title}
                       onChange={(e) => setForm({ ...form, event_title: e.target.value })} />
              </Field>
              <Field label="Nom de l'organisateur">
                <input data-testid="detail-organizer" className="field w-full" value={form.organizer_name}
                       onChange={(e) => setForm({ ...form, organizer_name: e.target.value })} />
              </Field>
              {typeOf(form.event_type)?.detail && (
                <Field label={typeOf(form.event_type).detail.label}>
                  <input className="field w-full" value={form.detail_value}
                         onChange={(e) => setForm({ ...form, detail_value: e.target.value })} />
                </Field>
              )}
            </div>
            {form.event_type && w.event_type !== form.event_type && (
              <p data-testid="detail-type-preview" className="mt-4 text-sm text-[#B8956A]">
                Nouveau type après enregistrement : « {typeOf(form.event_type)?.label} ». Les invités,
                menus, montants et historiques sont conservés.
              </p>
            )}
            {!!(w.type_history || []).length && (
              <p className="mt-3 text-xs text-[#697585]">
                {w.type_history.length} changement(s) de type tracé(s) dans l'historique.
              </p>
            )}
          </section>

          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Services de restauration</h2>
            <p className="mt-2 text-sm text-[#697585]">
              La complétude des invités dépend des services réellement prévus au contrat.
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <p className="overline">Adultes</p>
                <div className="mt-2 flex flex-wrap gap-4">
                  {Object.entries(SERVICE_LABELS).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" data-testid={`detail-adult-service-${k}`}
                             checked={form.adult_services.includes(k)}
                             onChange={() => setForm({
                               ...form,
                               adult_services: form.adult_services.includes(k)
                                 ? form.adult_services.filter((x) => x !== k)
                                 : [...form.adult_services, k],
                             })} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className="overline">Enfants</p>
                <div className="mt-2 flex flex-wrap gap-4">
                  {Object.entries(SERVICE_LABELS).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" data-testid={`detail-child-service-${k}`}
                             checked={form.child_services.includes(k)}
                             onChange={() => setForm({
                               ...form,
                               child_services: form.child_services.includes(k)
                                 ? form.child_services.filter((x) => x !== k)
                                 : [...form.child_services, k],
                             })} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <Field label="Propositions max. par service">
                <input className="field w-full" value={form.max_proposals_per_service}
                       onChange={(e) => setForm({ ...form, max_proposals_per_service: e.target.value })} />
              </Field>
              <label className="flex items-end gap-2 text-sm text-[#697585] pb-2">
                <input type="checkbox" data-testid="detail-collective-mode" checked={form.collective_mode}
                       onChange={(e) => setForm({ ...form, collective_mode: e.target.checked })} />
                Prestation collective (sans choix individuels)
              </label>
            </div>
            {(w.guests || []).some((g) => g.starter_id || g.main_id || g.dessert_id) && (
              <p className="mt-4 text-xs text-[#B8956A]">
                Attention : des choix sont déjà saisis. Désactiver un service les conserve mais les exclut
                du récapitulatif cuisine.
              </p>
            )}
          </section>

          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Date limite de finalisation</h2>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Mode">
                <select data-testid="detail-deadline-mode" className="field w-full" value={form.deadline_mode}
                        onChange={(e) => setForm({ ...form, deadline_mode: e.target.value })}>
                  <option value="relative">Automatique, X jours avant</option>
                  <option value="manual">Date fixée à la main</option>
                </select>
              </Field>
              {form.deadline_mode === "relative" ? (
                <Field label="Jours avant la réception">
                  <input data-testid="detail-deadline-days" className="field w-full" value={form.deadline_days_before}
                         onChange={(e) => setForm({ ...form, deadline_days_before: e.target.value })} />
                </Field>
              ) : (
                <Field label="Date limite">
                  <input data-testid="detail-deadline-date" type="date" className="field w-full"
                         value={form.finalization_deadline}
                         onChange={(e) => setForm({ ...form, finalization_deadline: e.target.value })} />
                </Field>
              )}
              <Field label="Motif (facultatif)">
                <input className="field w-full" value={form.deadline_reason}
                       onChange={(e) => setForm({ ...form, deadline_reason: e.target.value })} />
              </Field>
            </div>
            <p data-testid="detail-deadline-state" className="mt-4 text-sm text-[#697585]">
              {w.deadline_date
                ? `Échéance enregistrée : ${frDate(w.deadline_date)}${w.late ? " — dépassée, dossier à finaliser" : ""}`
                : "Aucune échéance enregistrée : réception à court délai, précisez la date convenue."}
            </p>
          </section>

          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Le dossier</h2>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Email de l'organisateur"><input data-testid="detail-email" className="field w-full" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
              <Field label="Téléphone"><input className="field w-full" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></Field>
              <Field label="Date"><input data-testid="detail-date" type="date" className="field w-full" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} /></Field>
              <Field label="Arrivée"><input className="field w-full" value={form.arrival_time} onChange={(e) => setForm({ ...form, arrival_time: e.target.value })} /></Field>
              <Field label="Début"><input className="field w-full" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></Field>
              <Field label="Fin prévue"><input className="field w-full" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></Field>
              <Field label="Fin maximale"><input className="field w-full" value={form.end_time_max} onChange={(e) => setForm({ ...form, end_time_max: e.target.value })} /></Field>
              <Field label="Espace">
                <select data-testid="detail-space" className="field w-full" value={form.space} onChange={(e) => setForm({ ...form, space: e.target.value })}>
                  <option value="">À confirmer</option>
                  <option value="petit_salon">Petit Salon</option>
                  <option value="grand_salon">Grand Salon</option>
                  <option value="etablissement">Établissement entier</option>
                </select>
              </Field>
              <label className="flex items-end gap-2 text-sm text-[#697585] pb-2">
                <input type="checkbox" data-testid="detail-space-confirmed" checked={form.space_confirmed} onChange={(e) => setForm({ ...form, space_confirmed: e.target.checked })} />
                Espace confirmé
              </label>
            </div>
            <p className="mt-4 text-xs text-[#697585]">
              Référence indicative avec cocktail pour {form.adults_count} adultes :
              {" "}{w.space_labels?.[w.space_reference] || "—"} (à confirmer par vos soins).
            </p>
          </section>

          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Tarifs & effectifs convenus</h2>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field label="Adulte (€)"><input data-testid="detail-adult-price" className="field w-full" value={form.adult_price} onChange={(e) => setForm({ ...form, adult_price: e.target.value })} /></Field>
              <Field label="Enfant (€)"><input data-testid="detail-child-price" className="field w-full" value={form.child_price} onChange={(e) => setForm({ ...form, child_price: e.target.value })} /></Field>
              <Field label="Âge enfant max"><input className="field w-full" value={form.child_max_age} onChange={(e) => setForm({ ...form, child_max_age: e.target.value })} /></Field>
              <Field label="Cocktail (€)"><input className="field w-full" value={form.cocktail_price} onChange={(e) => setForm({ ...form, cocktail_price: e.target.value })} /></Field>
              <Field label="Fromage (€)"><input className="field w-full" value={form.cheese_price} onChange={(e) => setForm({ ...form, cheese_price: e.target.value })} /></Field>
              <Field label="Demi-heure sup. (€)"><input className="field w-full" value={form.extra_half_hour} onChange={(e) => setForm({ ...form, extra_half_hour: e.target.value })} /></Field>
              <Field label="Minutes incluses"><input className="field w-full" value={form.included_minutes} onChange={(e) => setForm({ ...form, included_minutes: e.target.value })} /></Field>
              <Field label="Minutes sup."><input data-testid="detail-extra-minutes" className="field w-full" value={form.extra_minutes} onChange={(e) => setForm({ ...form, extra_minutes: e.target.value })} /></Field>
              <Field label="Adultes prévus"><input data-testid="detail-adults-count" className="field w-full" value={form.adults_count} onChange={(e) => setForm({ ...form, adults_count: e.target.value })} /></Field>
              <Field label="Enfants prévus"><input className="field w-full" value={form.children_count} onChange={(e) => setForm({ ...form, children_count: e.target.value })} /></Field>
              <Field label="Cocktail (pers.)"><input data-testid="detail-cocktail-participants" className="field w-full" value={form.cocktail_participants} onChange={(e) => setForm({ ...form, cocktail_participants: e.target.value })} /></Field>
              <Field label="Fromage (pers.)"><input className="field w-full" value={form.cheese_participants} onChange={(e) => setForm({ ...form, cheese_participants: e.target.value })} /></Field>
              <Field label="Min. factu. adultes"><input className="field w-full" value={form.min_billable_adults} onChange={(e) => setForm({ ...form, min_billable_adults: e.target.value })} /></Field>
              <Field label="Min. factu. enfants"><input className="field w-full" value={form.min_billable_children} onChange={(e) => setForm({ ...form, min_billable_children: e.target.value })} /></Field>
              <Field label="Acompte (%)"><input className="field w-full" value={form.deposit_percent} onChange={(e) => setForm({ ...form, deposit_percent: e.target.value })} /></Field>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Modalités de solde"><input className="field w-full" value={form.balance_terms} onChange={(e) => setForm({ ...form, balance_terms: e.target.value })} /></Field>
              <Field label="Modalités d'acompte"><input className="field w-full" value={form.deposit_terms} onChange={(e) => setForm({ ...form, deposit_terms: e.target.value })} /></Field>
            </div>
            <Field label="Conditions particulières">
              <textarea className="field w-full min-h-[120px]" value={form.conditions_text} onChange={(e) => setForm({ ...form, conditions_text: e.target.value })} />
            </Field>
            <div className="mt-6 bg-[#F6F0E7] p-5">
              <p className="overline">Budget convenu recalculé</p>
              <p data-testid="detail-budget-total" className="font-display text-3xl mt-2 text-[#B8956A]">{euros(w.budget?.total_cents)}</p>
              <p className="mt-2 text-xs text-[#697585]">
                Privatisation {euros(0)} · acompte {euros(w.budget?.deposit_cents)} · solde {euros(w.budget?.balance_cents)}
                {w.budget?.extra_half_hours ? ` · ${w.budget.extra_half_hours} demi-heure(s) sup.` : ""}
              </p>
              {!!w.legacy_privatisation_cents && (
                <p data-testid="legacy-privatisation-warning" className="mt-3 text-xs text-[#B8956A]">
                  Ce dossier historique porte un montant de privatisation de {euros(w.legacy_privatisation_cents)}.
                  Il n'a pas été modifié automatiquement : corrigez-le si nécessaire.
                </p>
              )}
            </div>
          </section>

          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Acceptation & acompte</h2>
            <p className="mt-2 text-sm text-[#697585]">
              L'acceptation et la réception d'un acompte sont deux informations distinctes.
            </p>
            <Field label="Preuve d'acceptation (extrait de l'échange)">
              <textarea data-testid="detail-acceptance-proof" className="field w-full min-h-[90px]" value={form.acceptance_proof} onChange={(e) => setForm({ ...form, acceptance_proof: e.target.value })} />
            </Field>
            <label className="mt-4 flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="detail-acceptance-confirmed" checked={form.acceptance_confirmed} onChange={(e) => setForm({ ...form, acceptance_confirmed: e.target.checked })} />
              Je confirme que cette proposition a été acceptée par le client
            </label>
            <div className="mt-4 flex flex-wrap items-end gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="detail-deposit-received" checked={form.deposit_received} onChange={(e) => setForm({ ...form, deposit_received: e.target.checked })} />
                Acompte reçu
              </label>
              <Field label="Montant reçu (€)"><input className="field w-40" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} /></Field>
            </div>
          </section>

          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Notes</h2>
            <Field label="Notes internes (jamais visibles par les mariés)">
              <textarea data-testid="detail-internal-notes" className="field w-full min-h-[90px]" value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} />
            </Field>
            <Field label="Fiche technique / notes opérationnelles (reprises dans le récap cuisine)">
              <textarea data-testid="detail-operations-notes" className="field w-full min-h-[120px]" value={form.operations_notes} onChange={(e) => setForm({ ...form, operations_notes: e.target.value })} />
            </Field>
          </section>

          <button data-testid="detail-save-btn" className="btn-primary inline-flex items-center gap-2" onClick={saveContract}>
            <Save size={14} /> Enregistrer le dossier
          </button>
        </div>
      )}

      {tab === "guests" && (
        <div className="mt-8 space-y-8">
          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Propositions retenues par l'organisateur</h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-5 text-sm">
              {[["starters", "Entrées"], ["mains", "Plats"], ["desserts", "Desserts"]].map(([k, label]) => (
                <div key={k}>
                  <p className="overline">{label}</p>
                  <ul className="mt-2 space-y-1">
                    {(w.menu_selection?.[k] || []).map((cid) => <li key={cid}>· {labels[cid] || cid}</li>)}
                    {!(w.menu_selection?.[k] || []).length && <li className="text-[#697585]">Aucune</li>}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Carte enfant autorisée</h2>
            <p className="mt-2 text-sm text-[#697585]">
              Cochez les plats et desserts que vous autorisez pour les enfants. Sans sélection, les enfants
              piochent dans les propositions adultes.            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {childItems.map((it) => {
                const on = (w.allowed_child_catalog || []).includes(it.id);
                return (
                  <label key={it.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      data-testid={`allowed-child-${it.id}`}
                      checked={on}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...(w.allowed_child_catalog || []), it.id]
                          : (w.allowed_child_catalog || []).filter((x) => x !== it.id);
                        patch({ allowed_child_catalog: next }, "Carte enfant mise à jour");
                      }}
                    />
                    {it.label} <span className="text-xs text-[#697585]">({it.course === "main" ? "plat" : "dessert"})</span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Invités ({(w.guests || []).length})</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.14em] text-[#697585]">
                    <th className="py-2 pr-4">Nom</th><th className="py-2 pr-4">Type</th><th className="py-2 pr-4">Présent</th>
                    <th className="py-2 pr-4">Entrée</th><th className="py-2 pr-4">Plat</th><th className="py-2 pr-4">Dessert</th>
                    <th className="py-2 pr-4">Allergies</th><th className="py-2">Exception</th>
                  </tr>
                </thead>
                <tbody>
                  {(w.guests || []).map((g) => (
                    <tr key={g.id} data-testid={`admin-guest-${g.id}`} className="border-t border-[#D9D3C5]">
                      <td className="py-2 pr-4">{g.name}</td>
                      <td className="py-2 pr-4">{g.kind === "child" ? "Enfant" : "Adulte"}</td>
                      <td className="py-2 pr-4">{g.attending ? "Oui" : "Non"}</td>
                      <td className="py-2 pr-4">{labels[g.starter_id] || "—"}</td>
                      <td className="py-2 pr-4">{labels[g.main_id] || "—"}</td>
                      <td className="py-2 pr-4">{labels[g.dessert_id] || "—"}</td>
                      <td className="py-2 pr-4">{g.allergies || g.diet_note || "—"}</td>
                      <td className="py-2">
                        {g.exception_requested ? (
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              data-testid={`approve-exception-${g.id}`}
                              checked={(w.approved_exception_guest_ids || []).includes(g.id)}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...(w.approved_exception_guest_ids || []), g.id]
                                  : (w.approved_exception_guest_ids || []).filter((x) => x !== g.id);
                                patch({ approved_exception_guest_ids: next }, "Exceptions mises à jour");
                              }}
                            />
                            {(w.approved_exception_guest_ids || []).includes(g.id) ? "Validée" : "À valider"}
                          </label>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!(w.guests || []).length && <p className="text-sm text-[#697585] mt-3">L'organisateur n'a pas encore saisi d'invités.</p>}
            </div>
          </section>

          {!!(w.change_requests || []).length && (
            <section className="bg-white border border-[#D9D3C5] p-6">
              <h2 className="font-display text-xl font-light">Demandes de modification</h2>
              <ul className="mt-4 space-y-3">
                {w.change_requests.map((cr) => (
                  <li key={cr.id} data-testid={`admin-cr-${cr.id}`} className="border border-[#D9D3C5] p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="overline">{cr.kind}</span>
                      <span className="text-xs uppercase tracking-widest text-[#B8956A]">{cr.state}</span>
                    </div>
                    <p className="mt-2 text-sm">{cr.message}</p>
                    {cr.state === "pending" && (
                      <div className="mt-3 flex gap-2">
                        <button data-testid={`cr-approve-${cr.id}`} className="btn-ghost" onClick={() => resolveCr(cr.id, "approved")}>Accepter</button>
                        <button data-testid={`cr-refuse-${cr.id}`} className="btn-ghost" onClick={() => resolveCr(cr.id, "refused")}>Refuser</button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {tab === "access" && (
        <div className="mt-8 space-y-8">
          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Accès de l'organisateur</h2>
            <p className="mt-2 text-sm text-[#697585]">
              {w.access?.active
                ? "Un lien d'accès est actif. Le renouveler invalide immédiatement le précédent."
                : "Aucun accès actif pour ce dossier."}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button data-testid="access-create-btn" className="btn-ghost inline-flex items-center gap-2" onClick={() => access(w.access?.active ? "rotate" : "create")}>
                <KeyRound size={14} /> {w.access?.active ? "Renouveler le lien" : "Générer un lien"}
              </button>
              {w.access?.active && (
                <button data-testid="access-revoke-btn" className="btn-ghost inline-flex items-center gap-2" onClick={() => access("revoke")}>
                  <RotateCcw size={14} /> Révoquer
                </button>
              )}
              <button data-testid="send-invitation-btn" className="btn-primary inline-flex items-center gap-2" onClick={sendInvitation}>
                <Mail size={14} /> Envoyer l'accès à {w.contact_email}
              </button>
            </div>
            {!w.acceptance?.accepted && (
              <p className="mt-4 text-sm text-[#B8956A]">
                Confirmez d'abord la proposition acceptée dans l'onglet « Contrat » pour pouvoir ouvrir un accès.
              </p>
            )}
            {!w.organizer_name && (
              <p data-testid="missing-organizer-warning" className="mt-2 text-sm text-[#B8956A]">
                Renseignez le nom de l'organisateur avant l'envoi de l'accès.
              </p>
            )}
            {link && (
              <div className="mt-6 bg-[#F6F0E7] p-4">
                <p className="overline">Lien personnel (affiché une seule fois)</p>
                <p data-testid="access-link" className="mt-2 text-xs break-all">{link}</p>
                <button className="btn-ghost mt-3 inline-flex items-center gap-2" onClick={() => navigator.clipboard?.writeText(link)}>
                  <Copy size={14} /> Copier
                </button>
              </div>
            )}
          </section>

          <section className="bg-white border border-[#D9D3C5] p-6">
            <h2 className="font-display text-xl font-light">Envois</h2>
            <ul className="mt-4 space-y-2">
              {(w.outbox || []).map((o) => (
                <li key={o.id} data-testid={`outbox-${o.id}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-dashed border-[#D9D3C5] pb-2 text-sm">
                  <span>{o.kind} → {o.to}</span>
                  <span className="flex items-center gap-3">
                    <span className={o.state === "sent" ? "text-emerald-700" : "text-[#B8956A]"}>{o.state}</span>
                    {o.state !== "sent" && (
                      <button
                        data-testid={`outbox-retry-${o.id}`}
                        className="text-xs uppercase tracking-widest text-[#B8956A]"
                        onClick={async () => { await api.post(`/admin/weddings/${id}/resend-outbox/${o.id}`); load(); }}
                      >
                        Réessayer
                      </button>
                    )}
                  </span>
                </li>
              ))}
              {!(w.outbox || []).length && <li className="text-sm text-[#697585]">Aucun envoi pour ce dossier.</li>}
            </ul>
          </section>
        </div>
      )}

      {tab === "kitchen" && (
        <div className="mt-8 space-y-6 print:mt-0">
          <div className="flex flex-wrap gap-3 print:hidden">
            <button data-testid="kitchen-print-btn" className="btn-ghost inline-flex items-center gap-2" onClick={() => window.print()}>
              <Printer size={14} /> Imprimer
            </button>
            <button data-testid="kitchen-csv-btn" className="btn-ghost inline-flex items-center gap-2" onClick={downloadCsv}>
              <Download size={14} /> Export CSV
            </button>
          </div>
          {kitchen && (
            <section data-testid="kitchen-summary" className="bg-white border border-[#D9D3C5] p-6">
              <h2 className="font-display text-2xl font-light">{kitchen.event_title} · {frDate(kitchen.event_date)}</h2>
              <p className="mt-1 text-sm text-[#B8956A]">{kitchen.event_type_label}{kitchen.organizer_name ? ` · ${kitchen.organizer_name}` : ""}</p>
              <p className="mt-2 text-sm text-[#697585]">
                Arrivée {kitchen.arrival_time || "—"} · {kitchen.start_time || "—"} → {kitchen.end_time || "—"} ·
                Espace {w.space_labels?.[kitchen.space] || "à confirmer"}
              </p>
              <p className="mt-3 text-sm">
                <strong data-testid="kitchen-adults">{kitchen.adults_present}</strong> adultes présents ·
                <strong data-testid="kitchen-children"> {kitchen.children_present}</strong> enfants ·
                {kitchen.absent} absents
                {(kitchen.min_billable_adults || kitchen.min_billable_children) ? (
                  <span className="text-[#697585]"> · minimum facturable : {kitchen.min_billable_adults} adultes / {kitchen.min_billable_children} enfants</span>
                ) : null}
              </p>
              <p className="mt-1 text-sm text-[#697585]">
                Cocktail : {kitchen.cocktail_participants} · Fromage : {kitchen.cheese_participants} ·
                Dernière validation : {kitchen.validated_at ? new Date(kitchen.validated_at).toLocaleString("fr-FR") : "non validé"}
              </p>

              {[["Entrées", "starters"], ["Plats adultes", "mains_adults"], ["Desserts adultes", "desserts_adults"],
                ["Plats enfants", "mains_children"], ["Desserts enfants", "desserts_children"]].map(([title, key]) => (
                !!(kitchen[key] || []).length && (
                  <div key={key} className="mt-6">
                    <p className="overline">{title}</p>
                    <ul className="mt-2">
                      {kitchen[key].map((i) => (
                        <li key={i.id} className="flex justify-between gap-6 text-sm border-b border-dashed border-[#D9D3C5] py-1.5">
                          <span>{i.label}</span><strong>{i.count}</strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              ))}

              {!!(kitchen.allergies || []).length && (
                <div className="mt-6">
                  <p className="overline">Allergies & exceptions</p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {kitchen.allergies.map((a, i) => (
                      <li key={i}>
                        <strong>{a.guest}</strong>{a.table ? ` · table ${a.table}` : ""} — {a.allergies || a.diet_note}
                        {a.exception_approved ? <span className="text-emerald-700"> · exception validée</span> : <span className="text-[#B8956A]"> · à valider</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {kitchen.operations_notes && (
                <div className="mt-6">
                  <p className="overline">Fiche technique</p>
                  <p className="mt-2 text-sm whitespace-pre-line">{kitchen.operations_notes}</p>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {tab === "history" && (
        <section className="mt-8 bg-white border border-[#D9D3C5] p-6">
          <h2 className="font-display text-xl font-light flex items-center gap-2"><History size={16} /> Historique</h2>
          <ul className="mt-4 space-y-2">
            {audit.map((a) => (
              <li key={a.id} className="text-sm border-b border-dashed border-[#D9D3C5] pb-2">
                <span className="text-[#697585]">{new Date(a.at).toLocaleString("fr-FR")}</span> ·
                <strong> {a.action}</strong> · {a.actor}
                {a.reason ? ` — ${a.reason}` : ""}
              </li>
            ))}
            {!audit.length && <li className="text-sm text-[#697585]">Aucun événement.</li>}
          </ul>
          {!!(w.reopen_history || []).length && (
            <div className="mt-6">
              <p className="overline">Réouvertures</p>
              <ul className="mt-2 space-y-1 text-sm">
                {w.reopen_history.map((r, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check size={12} className="text-[#B8956A]" />
                    {new Date(r.at).toLocaleString("fr-FR")} · {r.by} — {r.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
